<template>
  <es-dialog
    :title="title"
    :visible.sync="visible"
    :width="dialogWidth"
    :custom-class="['payment-dialog', { 'balance-pay': paymentMethod === PAYMENT_METHOD.BALANCE }]"
    border
    :before-close="close"
    :close-on-click-modal="false">
    <div class="content">
      <!-- 支付宝/微信支付方式 -->
      <div class="alipay-wechat" v-if="paymentMethod === PAYMENT_METHOD.ALIPAY || paymentMethod === PAYMENT_METHOD.WECHAT">
        <div class="pay-method">
          <img :src="paymentMethodIcon" :alt="this.paymentMethodDesc" />
          <span>{{ this.paymentMethodDesc }}</span>
        </div>
        <div class="pay-amount">
          <span class="unit">￥</span>
          <span>{{ payAmountDisplay }}</span>
        </div>
        <div class="pay-qrcode">
          <vue-qr :text="qrCode" :size="240"></vue-qr>
        </div>
      </div>
      <!-- 银行卡转账支付 -->
      <div class="bank-transfer" v-if="paymentMethod === PAYMENT_METHOD.BANK">
        <ul class="pay-tips">
          <li>
            <span>{{ $t('已为该订单生成专属汇款账号，是平台收款账号下设的专属子账号。') }}</span>
            <es-button variant="text" theme="primary" class="download-proof" @click="download" :loading="loading">
              {{ $t('下载证明') }}
            </es-button>
          </li>
          <li>
            <span>{{ $t('请准确按照订单金额对公汇款；') }}</span>
            <span class="out-time">{{ $t('72小时内未转账成功订单会超时关闭。') }}</span>
          </li>
          <li>{{ $t('汇款成功后，订单将在10分钟内生效') }}</li>
        </ul>
        <RemittanceInfoCard :bankPayInfo="bankPay" :loadingStatus="loadingStatus" :payAmountDisplay="payAmountDisplay" />
        <es-button variant="base" theme="primary" class="to-pay" @click="sureBankPay">{{ $t('去转账，等待支付结果') }}</es-button>
      </div>
      <!-- 账户余额支付 -->
      <div class="balance-pay" v-if="paymentMethod === PAYMENT_METHOD.BALANCE">
        <es-form
          :model="signPayForm"
          label-width="100%"
          :rules="rules"
          ref="signForm"
          @submit.native.prevent
          class="sign-pay-form"
          hide-required-asterisk
          size="middle"
          label-position="top">
          <es-form-item :label="$t(`签署密码`)" prop="signPassword">
            <es-input v-model.trim="signPayForm.signPassword" type="password" show-password></es-input>
          </es-form-item>
          <es-form-item label="" class="edit-sign-password-wrapper">
            <es-button variant="text" theme="primary" class="edit-sign-password" @click="editSignPassword">{{ $t('修改签署密码') }}</es-button>
          </es-form-item>
        </es-form>
      </div>
    </div>
    <div slot="footer" class="dialog-footer" v-if="paymentMethod === PAYMENT_METHOD.BALANCE">
      <es-button type="primary" @click="signPasswordPay">{{ $t('确定') }}</es-button>
    </div>
  </es-dialog>
</template>
<script>
import { PAYMENT_METHOD } from '@/consts/payment'
import RemittanceInfoCard from '@/components/RemittanceInfoCard'
import { debounce } from '@/util'
import { mapState } from 'vuex'
import MD5 from 'js-md5'
import { orderSourceMap } from '@esign/esign-client-jssdk'
import tsignDataTrack from '@tsign/tsigndata-sdk-js'
import vueQr from 'vue-qr'
export default {
  name: 'PaymentDialog',
  components: {
    RemittanceInfoCard,
    vueQr,
  },
  props: {
    visible: {
      type: Boolean,
      default: false,
    },
    qrCodeUrl: {
      type: String,
      default: require('@/assets/images/qywx.png'),
    },
    paymentMethod: {
      type: String,
      default: PAYMENT_METHOD.ALIPAY,
    },
    expressFee: {
      type: Number,
      default: 0,
    },
    // 顺手购商品金额
    tyingSceneAmount: {
      type: Number,
      default: 0,
    },
    selectTyingSaleSchemaList: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      PAYMENT_METHOD,
      titleMap: {
        [PAYMENT_METHOD.ALIPAY]: this.$t('支付宝支付'),
        [PAYMENT_METHOD.WECHAT]: this.$t('微信支付'),
        [PAYMENT_METHOD.BANK]: this.$t('银行转账（对公支付）'),
        [PAYMENT_METHOD.BALANCE]: this.$t('账户余额支付'),
      },
      paymentMethodIconMap: {
        [PAYMENT_METHOD.ALIPAY]: require('@/assets/icons/alipay.svg'),
        [PAYMENT_METHOD.WECHAT]: require('@/assets/icons/wechat.svg'),
      },
      loading: false,
      routerQuery: {},
      bankPay: {},
      loadingStatus: false,
      rules: {
        signPassword: [{ required: true, message: this.$t('请输入签署密码进行支付') }],
      },
      signPayForm: {
        signPassword: '',
      },
      ouid: '',
      needPay: 0,
      orderInfo: {},
      amount: 0, // 主商品需要支付剩余总金额（商品金额 - 已支付金额 + 运费）
      fee: 0,
      currentInvoiceId: '',
      qrCode: '',
      clock: null,
      paymentReportMap: {
        [PAYMENT_METHOD.ALIPAY]: '支付宝',
        [PAYMENT_METHOD.WECHAT]: '微信支付',
        [PAYMENT_METHOD.BANK]: '银行转账',
        [PAYMENT_METHOD.BALANCE]: '余额',
      },
      sourceChannel: Number(sessionStorage.getItem('source_channel')) || 0,
      tyingSaleOrders: [], // 下单顺手购订单信息
    }
  },
  computed: {
    ...mapState({
      userInfo: state => state.userInfo,
      isOrg: state => state.isOrg,
      tsignData: state => state.tsignData,
    }),
    title() {
      return this.titleMap[this.paymentMethod]
    },
    paymentMethodDesc() {
      return this.paymentMethod === PAYMENT_METHOD.ALIPAY ? this.$t('使用支付宝扫码支付') : this.$t('使用微信扫码支付')
    },
    paymentMethodIcon() {
      return this.paymentMethodIconMap[this.paymentMethod]
    },
    dialogWidth() {
      return this.paymentMethod === PAYMENT_METHOD.BALANCE ? '480px' : '754px'
    },
    payAmountDisplay() {
      return (this.amount + this.tyingSceneAmount)?.toFixed(2) || '0.00'
    },
    // 已下单支付页
    rechargeOnline() {
      return this.$route.name === 'RechargeOnline'
    },
  },
  mounted() {
    this.ouid = localStorage.getItem('ouid')
    this.routerQuery = this.$router.currentRoute.query
    // this.openBankPay('bank')
  },
  methods: {
    // 银行转账，对公支付等待跳转结果页
    sureBankPay() {
      this.$ajax(this.$url.bankpay(), {
        orderId: this.routerQuery.orderId,
        bankAccountNo: this.bankPay.bankAccountNo,
      }).then(res => {
        this.$router.replace({
          path: '/result',
          query: {
            ...this.routerQuery,
            invoiceId: this.currentInvoiceId,
            paymentMethod: this.paymentMethod, // 支付类型
          },
        })
      })
    },
    // 埋点上报方法
    reportSourceChannel(type, params) {
      const personOid = localStorage.getItem('ouid')
      const personGid = localStorage.getItem('guid')
      const data = {
        commodity_number: this.orderInfo.totalNum,
        commodity_name: this.orderInfo.commodityName,
        commodity_id: this.orderInfo.commodityId,
        user_type: this.isOrg ? '企业' : '个人',
        oid: this.isOrg ? this.userInfo.orgId : personOid,
        gid: this.isOrg ? this.userInfo.gid : personGid,
        pay_amount: this.amount?.toFixed(2) || '0.00',
        geren_oid: personOid,
        geren_gid: personGid,
        order_source: orderSourceMap[this.sourceChannel] || 'other',
        order_id: this.orderInfo.showId,
        payment_method: this.paymentReportMap[this.paymentMethod],
        ...params,
      }
      tsignDataTrack.track(type, data)
    },
    // 支付成功上报方法
    reportPaySuccess() {
      try {
        this.reportSourceChannel('order_pay_success', {
          bundling_commodity_id: '',
          bundling_commodity_name: '',
          bundling_commodity: false, // 是否搭售订单
        })
        (this.rechargeOnline ? this.selectTyingSaleSchemaList : this.tyingSaleOrders).forEach(item => {
          const commodityInfo = this.rechargeOnline ? item : this.selectTyingSaleSchemaList.find(selItem => selItem.tyingActiveConfigId === item.activeTyingConfigId)
          this.reportSourceChannel('order_bundlingPay_success', {
            bundling_commodity_id: item.saleInfoId || item.saleSchemaId,
            bundling_commodity_name: item.saleSchemaName || item.saleInfoName,
            bundling_commodity: true,

            pay_amount: item.price,
            commodity_id: commodityInfo.commodityId,
            commodity_name: commodityInfo.commodityName || commodityInfo.saleSchemaName || commodityInfo.saleInfoName,
            commodity_number: item.num || commodityInfo.totalNum,
            order_id: item.orderId,
          })
        })
      } catch (error) {
        console.log(error)
      }
    },
    // 页面初始化
    async pageInit(routerQuery) {
      if (routerQuery) {
        this.routerQuery = routerQuery
        this.tyingSaleOrders = routerQuery?.tyingSaleOrders || []
      }
      if (this.routerQuery.orderId) {
        const orderInfo = await this.getOrderDetail()
        this.orderInfo = orderInfo.data
        const resExpressFee = await this.getFee()
        this.fee = resExpressFee.data ? resExpressFee.data.fee : 0
        this.amount += this.fee
        resExpressFee.data && (this.currentInvoiceId = resExpressFee.data.invoiceId)

        return this.orderInfo
      } else {
        this.currentInvoiceId = this.routerQuery.invoiceId
        this.amount += parseFloat(this.routerQuery.fee) || this.expressFee || 0
      }
    },
    // 获取订单快递费
    getFee() {
      const params = {}

      return new Promise((resolve, reject) => {
        this.$ajax(this.$url.getExpressFee(this.routerQuery.orderId), params)
          .then(res => {
            resolve(res)
          })
          .catch(err => {
            reject(err)
          })
      })
    },
    async thirdPay() {
      const params = {}
      params.type = this.paymentMethod
      params.payOrderId = this.payOrderId
      const returnUrl = window.location.origin

      return new Promise((resolve, reject) => {
        this.$ajax(this.$url.thirdPartPay(params.type, params.payOrderId), { returnUrl })
          .then(res => {
            this.apply = res.data
            resolve(res)
          })
          .catch(err => {
            reject(err)
          })
      })
    },
    // 确认支付
    handleConfirmPayment: debounce(
      async function handleConfirmPayment() {
        if (this.paymentMethod === PAYMENT_METHOD.BANK) {
          this.loadingStatus = true
          this.openBankPay(PAYMENT_METHOD.BANK)
          this.$reportBusiness('business_monitor_event_start', 'order_pay', {
            appId: this.$route.query.appid,
            ouid: this.userInfo.gid,
            orgId: this.userInfo.orgId,
            paymentMethod: this.paymentMethod,
            payOrderId: this.payOrderId,
            stage: 'RechargeOnline',
          })
        } else {
          await this.getPayOrderId()
          this.$reportBusiness('business_monitor_event_start', 'order_pay', {
            appId: this.$route.query.appid,
            ouid: this.userInfo.gid,
            orgId: this.userInfo.orgId,
            paymentMethod: this.paymentMethod,
            payOrderId: this.payOrderId,
            stage: 'RechargeOnline',
          })
          if (this.paymentMethod === PAYMENT_METHOD.ALIPAY) {
            if (this.isDingtalk) {
              const resThirdPartPayment = await this.thirdPay()
              const div = document.createElement('div')
              /* 此处form就是后台返回接收到的数据 */
              div.innerHTML = resThirdPartPayment
              document.body.appendChild(div)
              document.forms[0].submit()
            } else {
              await this.QrCodePay('ALIPAY')
            }
          } else if (this.paymentMethod === PAYMENT_METHOD.WECHAT) {
            await this.QrCodePay('TENPAY')
          }
        }
      },
      2000,
      true
    ),
    // 获取订单详情
    async getOrderDetail() {
      const params = {}
      const oid = this.userInfo.orgId ? this.userInfo.orgId : this.ouid

      return new Promise((resolve, reject) => {
        this.$ajax(this.$url.getOrderDetailByOrderId(this.routerQuery.orderId, oid), params)
          .then(async res => {
            if (res.code === 20502002) {
              this.$message.error(res.message)
              this.$router.push('/Myorder')
            } else {
              if (res.data.paymentType === 3) {
                this.isPostPaid = true
                await this.getTableList()
              } else {
                this.needPay = (res.data.price - res.data.amountPaid).toFixed(2)
                this.amount = res.data.price - res.data.amountPaid
              }
              resolve(res)
            }
          })
          .catch(err => {
            reject(err)
          })
      })
    },
    async getTableList() {
      return new Promise((resolve, reject) => {
        const order = this.routerQuery.orderId
        this.$ajax(this.$url.volumes(order), {})
          .then(res => {
            this.tableData = res.data
            // 按量后付的话，重新赋值一下amount
            let tempAmount = 0
            res.data.forEach(val => {
              if (val.status !== 1) {
                tempAmount += parseFloat(val.produceCost) - parseFloat(val.amountPaid)
              }
            })
            this.needPay = tempAmount
            this.amount = tempAmount
            resolve(res)
          })
          .catch(err => {
            reject(err)
          })
      })
    },
    // 余额支付
    async walletPay() {
      const params = {}
      params.password = MD5(this.signPayForm.signPassword)

      return new Promise((resolve, reject) => {
        this.$ajax(this.$url.walletPay(this.payOrderId), params).then(() => {
          this.reportPaySuccess()
          this.$router.replace({
            path: '/result',
            query: {
              ...this.routerQuery,
              payOrderId: this.payOrderId,
              invoiceId: this.currentInvoiceId,
            },
          })
        })
      })
    },
    // 换取payOrderId
    async getPayOrderId() {
      const params = {}
      params.fee = this.routerQuery.fee || this.fee || 0
      if (this.routerQuery.orderId) {
        params.objects = []
        const item = {
          orderId: this.routerQuery.orderId,
          price: this.needPay,
        }
        // 待支付页面顺手购信息从详情接口获取通过props传递过来selectTyingSaleSchemaList，下单页顺手购信息下单时直接获取通过init方法直接同步过来tyingSaleOrders
        const tyingSaleOrders = (this.rechargeOnline ? this.selectTyingSaleSchemaList : this.tyingSaleOrders).map(item => {
          return {
            orderId: item.orderId,
            price: item.price,
          }
        })
        params.objects.push(item, ...tyingSaleOrders)
      }
      if (this.currentInvoiceId) {
        params.invoiceId = this.currentInvoiceId
      }
      let operation = ''
      if (this.routerQuery.orderId && this.currentInvoiceId) {
        operation = 'PAY_WITH_INVOICE'
      } else if (this.routerQuery.orderId && !this.currentInvoiceId) {
        operation = 'PAY_FOR_ORDER'
      } else {
        operation = 'PAY_FOR_INVOICE'
      }
      params.operation = operation

      return new Promise((resolve, reject) => {
        this.$ajax(this.$url.getPayorderId(), params)
          .then(res => {
            this.payOrderId = res.data
            resolve(res)
          })
          .catch(err => {
            reject(err)
          })
      })
    },
    // ====================== 分割线 =============================
    close() {
      this.clearClock()
      this.$emit('update:visible', false)
      this.$emit('close')
    },
    //
    download() {
      this.loading = true
      this.$ajax(this.$url.downloadBankCert(), {
        orderId: this.routerQuery.orderId,
        bankAccountNo: this.bankPay.bankAccountNo,
        companyCode: this.orderInfo.companyCode,
      })
        .then(res => {
          if (res.data) {
            window.open(res.data)
            this.loading = false
            this.$message.success(this.$t('证明文件生成完成'))
          } else {
            this.downloadAgain()
          }
        })
        .catch(() => {
          this.loading = false
        })
    },
    async QrCodePay(thirdPayMode) {
      const data = {
        thirdPayMode,
        returnUrl: window.location.origin,
      }
      await this.$ajax(this.$url.precreatePay(this.payOrderId), data).then(res => {
        this.qrCode = res.data.qrCode
        // this.qrCodeDialogShow = true
        this.setClock()
      })
    },
    clearClock() {
      clearInterval(this.clock)
    },
    // 微信支付设置定时器查询状态
    setClock() {
      this.clock = setInterval(() => {
        this.$ajax(this.$url.getPayOrderDetail(this.payOrderId)).then(res => {
          if (res.data.status === 'PAY_SUCCESS') {
            this.reportPaySuccess()
            this.$router.replace({
              path: '/result',
              query: {
                ...this.routerQuery,
                payOrderId: this.payOrderId,
                invoiceId: this.currentInvoiceId,
              },
            })
            this.clearClock()
          }
        })
      }, 2000)
    },
    openBankPay() {
      this.loadingStatus = true
      this.$ajax(this.$url.createbankAccountNoPay(this.routerQuery.orderId), {})
        .then(res => {
          this.bankPay = res.data
          this.loadingStatus = false
        })
        .catch(() => {
          this.loadingStatus = false
        })
    },
    editSignPassword() {
      window.open(this.$Dic.getDefaultURL().setPwdUrl)
    },
    signPasswordPay() {
      this.$refs.signForm.validate(valid => {
        if (valid) {
          this.walletPay()
        }
      })
    },
  },
}
</script>

<style lang="less" scoped>
/deep/.payment-dialog {
  background: linear-gradient(180deg, #f0f6ff 0%, #ffffff 100%);
}
/deep/ .balance-pay {
  background: #fff;
}
.content {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 16px 0 0;
}
.alipay-wechat {
  padding-bottom: 26px;
}
.pay-method {
  margin-bottom: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  img {
    width: 20px;
    margin-right: 8px;
  }
  span {
    color: #000;
    font-weight: 400;
    font-size: 16px;
    line-height: 22px;
  }
}
.pay-amount {
  font-weight: 600;
  font-size: 24px;
  line-height: 28px;
  text-align: center;
  font-weight: bold;
  color: #1063f1;
  margin-bottom: 24px;
  .unit {
    font-size: 16px;
  }
}
.pay-qrcode {
  width: 240px;
  height: 240px;
  border-radius: 10px;
  box-shadow: 0px 5.3px 13.26px 0px #1063f133;
  overflow: hidden;
  img {
    width: 100%;
    height: 100%;
  }
}
.bank-transfer {
  width: 640px;
  padding-bottom: 6px;
  display: flex;
  flex-direction: column;
}
.pay-tips {
  margin-bottom: 24px;
  border-radius: 4px;
  background: #e6f3ff;
  padding: 8px 24px;
  li {
    color: #333333;
    font-weight: 400;
    font-size: 14px;
    line-height: 22px;
    list-style-type: disc;
    display: list-item;
    list-style-position: inside;
  }
  .out-time {
    color: #ff3b30;
  }
}
.to-pay {
  margin-top: 24px;
  align-self: center;
}
.balance-pay {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  width: 100%;
  .sign-pay-form {
    width: 100%;
    /deep/ .es-form-item {
      margin-bottom: 20px;
      .es-form-item__label {
        line-height: 20px;
        height: 20px;
        margin-bottom: 6px;
      }
      .es-form-item__content {
        line-height: 36px;
      }
      .es-input__inner {
        height: 36px;
        line-height: 36px;
      }
    }
    .edit-sign-password-wrapper {
      margin-bottom: 0;
    }
  }
}
</style>
